import React, { useEffect, useMemo, useRef, useState } from 'react'
import { validateEmail } from '../../utils/validations'
import { CreateUserDto, EditUserDto, User } from '../../types/User'
import { Tribe } from '../../types/Tribe'
import { Team } from '../../types/Team'
import { useNavigate } from 'react-router'
import 'react-datepicker/dist/react-datepicker.css'
import { toast } from '../../utils/toast'
import { useLazyGetAllGuildsQuery } from '../../redux/guild.endpoints'
import { Guild } from '../../types/Guilds'
import {
  ExperienceLevel,
  TechnologyList,
  TechStackList,
} from '../../types/TechStacks'
import {
  GeneralInformationData,
  GeneralInformationSection,
  GeneralInformationSectionRef,
} from './GeneralInformationSection'
import {
  MemberSettingsData,
  MemberSettingsSection,
  MemberSettingsSectionRef,
} from './MemberSettingsSection'
import { MemberTechStackSection } from './MemberTechStackSection'

interface Props {
  handleCreateSubmit?: (args: CreateUserDto) => Promise<void>
  handleEditSubmit?: (args: EditUserDto) => Promise<void>
  displayErrorBanner?: boolean
  loading: boolean
  user?: User
  companyId: number
  tribes: Tribe[]
  teams: Team[]
  technologyList: TechnologyList[]
  experienceLevel: ExperienceLevel[]
  selectedTechStackListByUser?: TechStackList[]
}

export const EmployeeForm = ({
  handleCreateSubmit,
  handleEditSubmit,
  displayErrorBanner,
  loading,
  user,
  companyId,
  teams,
  tribes,
  technologyList,
  experienceLevel,
  selectedTechStackListByUser,
}: Props) => {
  const [guilds, setGuilds] = useState<Array<Guild>>([])
  const [selectedTechStackList, setSelectedTechStackList] = useState<
    TechStackList[]
  >([])
  const [generalInfo, setGeneralInfo] = useState<GeneralInformationData>({
    email: '',
    firstName: '',
    lastName: '',
    startDate: null,
    endDate: null,
    selectedGuild: -1,
  })
  const [memberSettings, setMemberSettings] = useState<MemberSettingsData>({
    isAdmin: false,
    isGuildMaster: false,
    isLeadership: false,
    isStaff: false,
    isGrowthWheelReviewer: false,
    isGrowthWheelEditor: false,
    selectedTribes: [],
    selectedTeams: [],
    teamAllocations: {},
    selectedGuildIdsForGrowthWheelReviewer: [],
    selectedTribeIdsForEmailNotification: [],
    selectedGuildIdsForEmailNotification: [],
  })
  const generalInfoRef = useRef<GeneralInformationSectionRef>(null)
  const memberSettingsRef = useRef<MemberSettingsSectionRef>(null)
  const navigate = useNavigate()
  const [getAllGuilds, getAllGuildsResponse] = useLazyGetAllGuildsQuery()

  useEffect(() => {
    if (selectedTechStackListByUser) {
      setSelectedTechStackList(selectedTechStackListByUser)
    }
  }, [selectedTechStackListByUser])

  useEffect(() => {
    if (getAllGuildsResponse && getAllGuildsResponse.data) {
      setGuilds(getAllGuildsResponse.data)
    }
  }, [getAllGuildsResponse])

  useEffect(() => {
    getAllGuilds()
  }, [])

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const info = generalInfoRef.current?.getData() ?? generalInfo
    const settings = memberSettingsRef.current?.getData() ?? memberSettings

    if (settings.selectedTeams.length > 0) {
      let totalAllocation = 0
      Object.keys(settings.teamAllocations).forEach(key => {
        totalAllocation += settings.teamAllocations[parseInt(key)].allocation
      })
      if (totalAllocation > 100) {
        memberSettingsRef.current?.setAllocationError(true)
        toast.error('Total teams allocation can not be over 100%', {
          toastId: 'allocation-error',
        })
      } else if (validateEmail(info.email)) {
        const allocations: {
          [key: number]: { allocation: number; role: string }
        } = {}
        Object.keys(settings.teamAllocations).map(key => {
          const id = parseInt(key)
          allocations[id] = settings.teamAllocations[id]
        })
        const args = {
          isGrowthWheelReviewer: settings.isGrowthWheelReviewer,
          isGrowthWheelEditor: settings.isGrowthWheelEditor,
          guildIdsForGrowthWheelReviewer:
            settings.selectedGuildIdsForGrowthWheelReviewer,
          email: info.email.trim(),
          firstName: info.firstName.trim(),
          lastName: info.lastName.trim(),
          isAdmin: settings.isAdmin,
          isGuildMaster: settings.isGuildMaster,
          isLeadership: settings.isLeadership,
          guildId: info.selectedGuild,
          teamAllocations: allocations,
          startDate: info.startDate,
          endDate: info.endDate,
          tribes: settings.selectedTribes,
          staffAugmentation: settings.isStaff,
          subscribedTribeIdsForEmail:
            settings.selectedTribeIdsForEmailNotification,
          subscribedGuildIdsForEmail:
            settings.selectedGuildIdsForEmailNotification,
          techStack: selectedTechStackList,
        }
        user
          ? handleEditSubmit &&
            (await handleEditSubmit({
              ...args,
              userId: user.id,
            }))
          : handleCreateSubmit &&
            (await handleCreateSubmit({
              ...args,
              companyId,
            }))
      }
    } else {
      const args = {
        isGrowthWheelReviewer: settings.isGrowthWheelReviewer,
        isGrowthWheelEditor: settings.isGrowthWheelEditor,
        guildIdsForGrowthWheelReviewer:
          settings.selectedGuildIdsForGrowthWheelReviewer,
        email: info.email.trim(),
        firstName: info.firstName.trim(),
        lastName: info.lastName.trim(),
        isAdmin: settings.isAdmin,
        isGuildMaster: settings.isGuildMaster,
        isLeadership: settings.isLeadership,
        guildId: info.selectedGuild,
        teamAllocations: [],
        startDate: info.startDate,
        endDate: info.endDate,
        tribes: settings.selectedTribes,
        staffAugmentation: settings.isStaff,
        subscribedTribeIdsForEmail:
          settings.selectedTribeIdsForEmailNotification,
        subscribedGuildIdsForEmail:
          settings.selectedGuildIdsForEmailNotification,
        techStack: selectedTechStackList,
      }
      user
        ? handleEditSubmit &&
          (await handleEditSubmit({
            ...args,
            userId: user.id,
          }))
        : handleCreateSubmit &&
          (await handleCreateSubmit({
            ...args,
            companyId,
          }))
    }
  }

  const handleOnBack = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    navigate(-1)
  }

  const isDisabled = useMemo(() => {
    const skipsTeamRequirements =
      memberSettings.isLeadership || memberSettings.isStaff

    return (
      !validateEmail(generalInfo.email) ||
      !generalInfo.firstName ||
      !generalInfo.lastName ||
      !generalInfo.startDate ||
      generalInfo.selectedGuild === -1 ||
      (!skipsTeamRequirements &&
        (memberSettings.selectedTribes.length === 0 ||
          memberSettings.selectedTeams.length === 0)) ||
      loading
    )
  }, [generalInfo, memberSettings, loading])

  return (
    <>
      <form
        id="employee-form"
        className="employee-form"
        onSubmit={handleFormSubmit}
      >
        <GeneralInformationSection
          ref={generalInfoRef}
          guilds={guilds}
          user={user}
          displayErrorBanner={displayErrorBanner}
          onChange={setGeneralInfo}
          onGuildChange={() =>
            memberSettingsRef.current?.clearTeamsAndAllocations()
          }
        />

        <MemberSettingsSection
          ref={memberSettingsRef}
          guilds={guilds}
          tribes={tribes}
          teams={teams}
          selectedGuild={generalInfo.selectedGuild}
          user={user}
          onChange={setMemberSettings}
        />

        <MemberTechStackSection
          technologies={technologyList || []}
          experienceLevel={experienceLevel || []}
          selectedTechStackList={selectedTechStackList}
          onSelectedTechStackListChange={setSelectedTechStackList}
        />
      </form>

      <div className="employee-form__actions">
        <button
          type="button"
          className="employee-form__btn employee-form__btn--cancel"
          onClick={handleOnBack}
          aria-label="Cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          form="employee-form"
          className="employee-form__btn employee-form__btn--submit"
          disabled={isDisabled}
          aria-label="login-submit-button"
        >
          {user ? 'Update' : 'Save'}
        </button>
      </div>
    </>
  )
}
